import type { CSSProperties } from 'react';

// ----------------------------------------------------------------------
// V2 Design System — Typography based on Figma tokens
// Font: Inter
// ----------------------------------------------------------------------

declare module '@mui/material/styles' {
  interface TypographyVariants {
    fontWeightSemiBold: CSSProperties['fontWeight'];
  }
  interface TypographyVariantsOptions {
    fontWeightSemiBold?: CSSProperties['fontWeight'];
  }
}

export function pxToRem(value: number) {
  return `${value / 16}rem`;
}

const primaryFont = 'Inter, sans-serif';

// ----------------------------------------------------------------------
// Figma defines two modes: "desktop" and "mobile".
// MUI handles responsive via responsiveFontSizes.
// Desktop = lg breakpoint, Mobile = base fontSize.
//
// Figma desktop sizes:
//   h1=2rem(32px), h2=1.5rem(24px), h3=1.25rem(20px), h4=1.125rem(18px),
//   h5=1rem(16px), h6=0.875rem(14px), subtitle1=1.125rem(18px),
//   subtitle2=1rem(16px), body1=0.875rem(14px), body2=0.75rem(12px),
//   caption=0.75rem(12px), overline=0.75rem(12px)
//
// Figma mobile sizes:
//   h1=2.375rem(38px — larger on mobile for hero), h2=1.5rem, h3=1.25rem,
//   h4=1.125rem, h5=1rem, h6=0.875rem, subtitle1=1.125rem,
//   subtitle2=1rem, body1=0.875rem, body2=1rem(16px — larger on mobile),
//   caption=0.75rem, overline=0.75rem
// ----------------------------------------------------------------------

// TODO aggiornare i Weight e Letter Spacing di h5, h6, subtitle1, subtitle2, body1, body2,
// overline quando disponibili da design. NB: subtitle1 (18px) coincide con h4 ed è più grande
// di h5 (16px) — da confermare, non "correggere" a occhio.

export const typographyV2 = {
  fontFamily: primaryFont,
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightSemiBold: 600,
  fontWeightBold: 700,
  // Flat 32px: Figma's 38px h1 belongs to its "mobile" mode, but this is a desktop-first
  // app, so scaling the global h1 *down* as the viewport grows read as a bug. A 38px hero
  // wants its own scoped style, not the global h1.
  h1: {
    fontWeight: 700,
    lineHeight: 1.25,
    fontSize: pxToRem(32),
  },
  h2: {
    fontWeight: 500,
    lineHeight: 1.33,
    letterSpacing: '-0.5px',
    fontSize: pxToRem(24),
  },
  // 20px per Figma. This used to duplicate h2 byte-for-byte, which left the app with no
  // h3 step at all: `variant="h2"` and `variant="h3"` rendered identically.
  h3: {
    fontWeight: 500,
    lineHeight: 1.4,
    letterSpacing: '-0.5px',
    fontSize: pxToRem(20),
  },
  h4: {
    fontWeight: 500,
    lineHeight: 1.44,
    letterSpacing: '0.25px',
    fontSize: pxToRem(18),
  },
  h5: {
    fontWeight: 500,
    lineHeight: 1.5,
    fontSize: pxToRem(16),
  },
  h6: {
    fontWeight: 500,
    lineHeight: 1.57,
    fontSize: pxToRem(14),
  },
  subtitle1: {
    fontWeight: 500,
    lineHeight: 1.5,
    fontSize: pxToRem(18),
  },
  subtitle2: {
    fontWeight: 500,
    lineHeight: 1.5,
    fontSize: pxToRem(16),
  },
  body1: {
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: '0.15px',
    fontSize: pxToRem(14),
  },
  body2: {
    fontWeight: 400,
    lineHeight: 1.5,
    fontSize: pxToRem(12), // desktop: 0.75rem, mobile: 1rem
  },
  caption: {
    fontWeight: 400,
    lineHeight: 1.33,
    letterSpacing: '0.4px',
    fontSize: pxToRem(12),
  },
  overline: {
    fontWeight: 600,
    lineHeight: 1.5,
    fontSize: pxToRem(12),
    textTransform: 'uppercase',
  },
  // 500/'none' to match the MuiButton override, which wins over these anyway — the token
  // said 600/'unset' and was silently never applied.
  button: {
    fontWeight: 500,
    lineHeight: 24 / 14,
    fontSize: pxToRem(14),
    textTransform: 'none',
  },
} as const;
